import { Audio, AVPlaybackStatus, AVPlaybackStatusToSet } from 'expo-av';
import React, {
  createContext,
  useEffect,
  useReducer,
  useRef,
  ReactNode,
} from 'react';
import DownloadManager from '@/utils/DownloadManager';
import {
  emptyAudioCache,
  promisify,
  reducer,
  setAudioCache,
  setAudioModeSetup,
  trackedDataSetup,
} from './helpers';
import { INITIAL_STATE } from './constants';
import { AudioContextValue, AudioPayload } from './types';
import { logError } from '@/utils/logHandling';

export const AudioContext = createContext<AudioContextValue | undefined>(
  undefined,
);

export const AudioContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [context, dispatch] = useReducer(reducer, INITIAL_STATE);
  const audioInstance = useRef<Audio.Sound>(new Audio.Sound());
  const trackedKey = useRef<string | null>(null);
  const trackedData = useRef<number[] | null>(null);
  const wasPlayerPlayingBeforePause = useRef<boolean>(false);

  const alterContext = (payload: AudioPayload) => {
    dispatch({
      type: 'KEY_CHANGE',
      payload,
    });
  };

  const resetContext = () => {
    alterContext({
      source: '',
      position: 0,
      duration: 0,
      isPlaying: false,
      isLoading: true,
      playbackRate: 1,
    });
  };

  const getAudioStatus = async () => {
    try {
      const status = await audioInstance.current.getStatusAsync();
      return status;
    } catch (error) {
      logError('Could not get audio status', error);
    }
  };

  const onPlaybackStatusUpdate = (status: AVPlaybackStatus) => {
    if (status.isLoaded) {
      (async () => {
        const position = status.positionMillis;
        const duration = status.durationMillis;
        const value = `${position}-${duration}`;
        await setAudioCache(trackedKey.current, value);
      })();
      alterContext({
        isPlaying: status.isPlaying,
        position: status.positionMillis || 0,
        duration: status.durationMillis || 0,
      });
    }
  };

  const init = async (
    uri: string,
    autoPlay: boolean,
    isTracked: boolean,
    uniqueKey: string,
    playbackRate?: number,
  ) => {
    try {
      await setAudioModeSetup();
      // Unloading any previously loaded sounds (to maintain only one instance)
      if (isTracked && uniqueKey) {
        const cachedData = await trackedDataSetup(uniqueKey);
        if (cachedData) {
          const cachedDataNumbers = cachedData.map((eachCache) =>
            Number(eachCache),
          );
          trackedData.current = cachedDataNumbers;
          alterContext({
            source: uri,
            position: cachedDataNumbers[0],
            duration: cachedDataNumbers[1],
            isPlaying: false,
            isLoading: false,
          });
        } else {
          trackedData.current = null;
        }
      } else {
        trackedData.current = null;
      }
      if (!autoPlay)
        return alterContext({
          isLoading: false,
        });
      await unloadSound();
      await loadAndPlaySound(uri, uniqueKey, playbackRate);
    } catch (error) {
      logError('Error while initializing audio instance:', error);
      throw error;
    }
  };

  const loadAndPlaySound = async (
    uri: string,
    uniqueKey: string,
    playbackRate = 1,
  ) => {
    try {
      audioInstance.current.setOnPlaybackStatusUpdate(onPlaybackStatusUpdate);
      const prevSource = context.source + (trackedKey.current ?? '');
      const currentSource = uri + (uniqueKey ?? '');
      const isSameAudio = prevSource === currentSource;
      const status = await getAudioStatus();
      // This is true when the player was played & paused without changing the instance
      if (isSameAudio && status?.isLoaded) return await playSound();
      await unloadSound();
      // Here we are setting up the new audio
      trackedKey.current = uniqueKey ?? null;
      const offlineURI = await DownloadManager.getFileUri(uri);

      const initialStatus: AVPlaybackStatusToSet = {
        positionMillis: trackedData.current ? trackedData.current[0] : 0,
      };

      await audioInstance.current.loadAsync({ uri: offlineURI }, initialStatus);
      await audioInstance.current.setRateAsync(playbackRate, true);

      const getAudioDuration = async (
        resolve: (duration: number | undefined) => void,
      ) => {
        const status = await getAudioStatus();
        if (!status?.isLoaded) return;
        const { durationMillis } = status;
        resolve(durationMillis);
      };

      const audioDuration = await promisify<number>(getAudioDuration, 100);
      alterContext({
        source: uri,
        position: initialStatus.positionMillis,
        duration: audioDuration,
        isLoading: false,
      });
      await playSound(trackedData.current);
    } catch (error) {
      logError('Could not load or play sound', error);
    }
  };

  const playSound = async (trackedData?: number[] | null) => {
    try {
      if (trackedData) {
        const didFinish = trackedData[0] / trackedData[1] >= 0.99;
        if (didFinish) {
          await audioInstance.current.replayAsync();
        } else {
          await audioInstance.current.playFromPositionAsync(trackedData[0]);
        }
      } else {
        const didFinish = context.position / context.duration >= 0.99;
        if (didFinish) await audioInstance.current.replayAsync();
        else await audioInstance.current.playAsync();
      }
    } catch (error) {
      logError('Could not play sound', error);
    }
  };

  const pauseSound = async () => {
    try {
      const status = await getAudioStatus();
      if (!status?.isLoaded) return;
      wasPlayerPlayingBeforePause.current = status?.isPlaying;
      await audioInstance.current.pauseAsync();
    } catch (error) {
      logError('Error while pausing running audio', error);
    }
  };

  const stopSound = async () => {
    try {
      const status = await getAudioStatus();
      if (!status?.isLoaded) return;
      await audioInstance.current.stopAsync();
    } catch (error) {
      logError('Error while stopping running audio', error);
    }
  };

  const unloadSound = async () => {
    try {
      const status = await getAudioStatus();
      if (!status?.isLoaded) return;
      await audioInstance.current.unloadAsync();
    } catch (error) {
      logError('Error while unloading active audio', error);
    }
  };

  const changeAudioPosition = async (value: number) => {
    try {
      const status = await getAudioStatus();
      if (!status?.isLoaded || !status.durationMillis) return;
      const newPosition = value * status.durationMillis;
      alterContext({ position: newPosition });
      const cachedValue = `${newPosition}-${status.durationMillis}`;
      await Promise.all([
        audioInstance.current.setPositionAsync(newPosition),
        setAudioCache(trackedKey.current, cachedValue),
      ]);
      if (!wasPlayerPlayingBeforePause.current) return;
      await playSound();
    } catch (error) {
      logError('ERROR while changing audio position', error);
    }
  };

  const moveBack = async () => {
    try {
      const status = await getAudioStatus();
      if (!status?.isLoaded) return;
      await pauseSound();
      const currentPosition = status.positionMillis;
      const secondsInMil = 15000;
      const expectedPosition = currentPosition - secondsInMil;
      const derivedPosition = Math.max(expectedPosition, 0);
      await audioInstance.current.playFromPositionAsync(derivedPosition);
    } catch (error) {
      logError('Could not pause sound', error);
    }
  };

  useEffect(() => {
    return () => {
      unloadSound();
      emptyAudioCache();
    };
  }, []);

  const value: AudioContextValue = {
    init,
    loadAndPlaySound,
    pauseSound,
    stopSound,
    unloadSound,
    changeAudioPosition,
    moveBack,
    context,
    resetContext,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
};
